import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  Chip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useFormik } from "formik";
import * as Yup from "yup";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  fetchPurgePreview,
  purgeUser,
  resetGdprState,
} from "../../redux/slices/superadminGdpr";
import { PurgeUserResult } from "../../types/superadminGdpr";

interface GdprPurgeDialogProps {
  open: boolean;
  userId: number | null;
  onClose: () => void;
  onPurged: (result: PurgeUserResult) => void;
}

const counterRow = (label: string, value: number) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    sx={{ py: 0.5 }}
    key={label}
  >
    <Typography variant="body2">{label}</Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: value > 0 ? 600 : 400 }}
      color={value > 0 ? "warning.main" : "text.secondary"}
    >
      {value}
    </Typography>
  </Stack>
);

export const GdprPurgeDialog: React.FC<GdprPurgeDialogProps> = ({
  open,
  userId,
  onClose,
  onPurged,
}) => {
  const dispatch = useAppDispatch();
  const { preview, previewStatus, previewError, purgeStatus, purgeError } =
    useAppSelector((state) => state.superadminGdpr);

  const [selectedAssocIds, setSelectedAssocIds] = useState<
    Record<number, boolean>
  >({});

  useEffect(() => {
    if (open && userId !== null) {
      dispatch(fetchPurgePreview(userId));
    }
    if (!open) {
      dispatch(resetGdprState());
      setSelectedAssocIds({});
    }
  }, [open, userId, dispatch]);

  useEffect(() => {
    if (preview) {
      const defaults: Record<number, boolean> = {};
      for (const a of preview.associations) {
        defaults[a.id] = a.otherAdmins === 0 && !a.realActivity;
      }
      setSelectedAssocIds(defaults);
    }
  }, [preview]);

  const formik = useFormik({
    initialValues: { reason: "", confirmEmail: "" },
    enableReinitialize: true,
    validationSchema: Yup.object({
      reason: Yup.string()
        .trim()
        .min(10, "Anna vähintään 10 merkin perustelu")
        .required("Perustelu vaaditaan"),
      confirmEmail: Yup.string()
        .trim()
        .lowercase()
        .oneOf(
          preview ? [preview.user.email.trim().toLowerCase()] : [],
          "Sähköposti ei täsmää"
        )
        .required("Vahvistus vaaditaan"),
    }),
    onSubmit: async (values) => {
      if (userId === null) return;
      const deleteAssociationIds = Object.entries(selectedAssocIds)
        .filter(([, v]) => v)
        .map(([k]) => Number(k));
      const result = await dispatch(
        purgeUser({
          userId,
          payload: {
            reason: values.reason.trim(),
            deleteAssociationIds,
            confirmEmail: values.confirmEmail.trim(),
          },
        })
      );
      if (purgeUser.fulfilled.match(result)) {
        onPurged(result.payload as PurgeUserResult);
      }
    },
  });

  const personalDataRows = useMemo(() => {
    if (!preview) return [];
    const p = preview.personalData;
    return [
      ["Kiinteistöjä", p.properties],
      ["Laskuja (säilyvät, omistaja nollataan)", p.invoices],
      ["Tositteita (säilyvät, omistaja nollataan)", p.vouchers],
      ["Viestejä", p.messages],
      ["Keskusteluita", p.conversations],
      ["Kokouskutsuja", p.meetingInvites],
      ["Tiedoksiantoja", p.noticeDeliveries],
      ["Sähköpostilokirivejä", p.emailLogs],
    ] as Array<[string, number]>;
  }, [preview]);

  const isSubmitting = purgeStatus === "loading";

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Poista käyttäjä ja siihen liittyvät tiedot (GDPR)
      </DialogTitle>
      <DialogContent dividers>
        {previewStatus === "loading" && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {previewError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {previewError}
          </Alert>
        )}
        {preview && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tämä toiminto on peruuttamaton. Toimet kirjataan
              GDPR-poistolokiin.
            </Alert>

            <Typography variant="subtitle2" gutterBottom>
              Käyttäjä
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>
                  {preview.user.firstName} {preview.user.lastName}
                </strong>{" "}
                — {preview.user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {preview.user.id} · Luotu:{" "}
                {new Date(preview.user.createdAt).toLocaleDateString("fi-FI")} ·{" "}
                {preview.user.isVerified ? "Aktivoitu" : "Ei aktivoitu"}
                {preview.user.isProtected ? " · SUOJATTU" : ""}
                {preview.user.isSuperAdmin ? " · PÄÄKÄYTTÄJÄ" : ""}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Henkilökohtaiset tiedot
            </Typography>
            <Box sx={{ mb: 2 }}>
              {personalDataRows.map(([label, value]) =>
                counterRow(label, value)
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Tiehoitokunnat
            </Typography>
            {preview.associations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Käyttäjä ei kuulu yhteenkään tiehoitokuntaan.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Poista</TableCell>
                    <TableCell>Tiehoitokunta</TableCell>
                    <TableCell>Roolit</TableCell>
                    <TableCell align="right">Muita ylläpitäjiä</TableCell>
                    <TableCell align="right">Muita jäseniä</TableCell>
                    <TableCell>Aktiviteetti</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.associations.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selectedAssocIds[a.id]}
                          onChange={(e) =>
                            setSelectedAssocIds((prev) => ({
                              ...prev,
                              [a.id]: e.target.checked,
                            }))
                          }
                        />
                      </TableCell>
                      <TableCell>{a.name}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {a.roles.map((r) => (
                            <Chip key={r} label={r} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{a.otherAdmins}</TableCell>
                      <TableCell align="right">{a.otherMembers}</TableCell>
                      <TableCell>
                        {a.realActivity ? (
                          <Tooltip
                            title={
                              a.realActivityReasons.join(", ") ||
                              "Aktiviteettia havaittu"
                            }
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <WarningAmberIcon
                                color="warning"
                                fontSize="small"
                              />
                              <Typography variant="caption">
                                {a.realActivityReasons.join(", ")}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            ei aktiviteettia
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Divider sx={{ my: 2 }} />

            <form onSubmit={formik.handleSubmit} id="gdpr-purge-form">
              <TextField
                name="reason"
                label="Perustelu (vähintään 10 merkkiä, kirjataan lokiin)"
                fullWidth
                multiline
                rows={2}
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                sx={{ mb: 2 }}
              />
              <TextField
                name="confirmEmail"
                label={`Vahvista kirjoittamalla "${preview.user.email}"`}
                fullWidth
                value={formik.values.confirmEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmEmail &&
                  Boolean(formik.errors.confirmEmail)
                }
                helperText={
                  formik.touched.confirmEmail && formik.errors.confirmEmail
                }
              />
            </form>

            {purgeError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {purgeError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Peruuta
        </Button>
        <Button
          type="submit"
          form="gdpr-purge-form"
          color="error"
          variant="contained"
          disabled={!preview || isSubmitting || !formik.isValid}
        >
          {isSubmitting ? <CircularProgress size={20} /> : "Poista pysyvästi"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GdprPurgeDialog;
